/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AbstractFormComponent, BuilderNavigationService, FormFlowState } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import {
  ActionFlow, AppRouter,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
  getRouteParentTitle,
  getRouteQueryParam,
  ImmutablePageState,
  isRouteForCreationMode,
  PAGE_STATE,
  queryDirty,
  queryFormEditable,
  queryFormMode,
  queryRecalculationNeeded,
  State,
  UserContext,
} from '@gh/common-shared';
import { FormMode } from '@gh/core-ui';
import { openInNewWindow } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { PbDefaults, PbMasterInfo } from '../../resources/price-book.model';
import { PbCommandService } from './pb-command.service';
import { pbDetailsReducer } from './pb-details.reducers';
import {
  PB_DETAILS_INIT_STATE,
  PbDetailsState,
  PbDetailsTab,
  queryMemo,
  queryPbDefaults,
  queryPbMasterInfo,
  queryPriceBookId,
  querySelectedTab,
} from './pb-details.state';
import { TranslationTupleOrId } from '@gh/core-mls';

export function pbDetailsStateFactory(): State<PbDetailsState> {
  return new State(PB_DETAILS_INIT_STATE, pbDetailsReducer);
}

export function pbDetailsFormFactory(state: State<PbDetailsState>): ImmutablePageState<PbDetailsState> {
  return new ImmutablePageState(state, {
    formMode: queryFormMode,
    dirty: queryDirty,
  });
}

@Component({
  standalone: false,
  selector: 'gh-price-book-details',
  templateUrl: 'price-book-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    PbCommandService,
    { provide: State, useFactory: pbDetailsStateFactory },
    { provide: PAGE_STATE, useFactory: pbDetailsFormFactory, deps: [State] },
  ],
})
export class PriceBookDetailsComponent extends AbstractFormComponent<PbDetailsState> implements OnInit {
  recalculationNeeded$: Observable<boolean>;
  defaults$: Observable<FormStateSnapshot<PbDefaults>>;
  masterInfo$: Observable<FormStateSnapshot<PbMasterInfo>>;
  name$: Observable<string>;
  memo$: Observable<string>;
  selectedTab$: Observable<PbDetailsTab>;
  editable: boolean;

  private reportUrl: string;

  constructor(private route: ActivatedRoute,
              @Inject(PAGE_STATE) public form: ImmutablePageState<PbDetailsState>,
              private state: State<PbDetailsState>,
              private commands: PbCommandService,
              private router: AppRouter,
              private userContext: UserContext,
              private navigationService: BuilderNavigationService) {
    super();
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_price_books';
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_pb_create',
      [FormMode.EditMode]: 'lbl_pb_edit',
      [FormMode.ViewMode]: 'lbl_pb_view',
    }[this.form.mode];
  }

  get completeFlow(): ActionFlow<FormFlowState<PbDetailsState>> {
    return this.commands.completeFlow();
  }

  ngOnInit(): void {
    const { details, reportUrl } = getRouteInitData(this.route);

    const mode = isRouteForCreationMode(this.route)
      ? FormMode.CreateMode : (this.userContext.hasPermission(BuilderPermission.Estimating.PriceBook.EDIT)
        ? FormMode.EditMode : FormMode.ViewMode);

    this.commands.load(details, mode, getRouteQueryParam(this.route, 'cloneFrom'));

    this.reportUrl = reportUrl;

    this.editable = this.state.project(queryFormEditable);
    this.defaults$ = this.state.select(queryPbDefaults);
    this.masterInfo$ = this.state.select(queryPbMasterInfo);
    this.name$ = this.masterInfo$.pipe(map$(getFormStateSnapshotValue), map$(({ name }) => name));
    this.recalculationNeeded$ = this.state.select(queryRecalculationNeeded);
    this.memo$ = this.state.select(queryMemo);
    this.selectedTab$ = this.state.select(querySelectedTab);
  }

  onRecalculate(): void {
    const flow = this.commands.recalculateFlow();

    flow.run({ form: this.form, value: this.form.submitValue });
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityType: 'PB',
      entityId: this.state.project(queryPriceBookId),
      label: 'lbl_price_book',
    });
  }

  onReprice(): void {
    this.commands.reprice();
  }

  onApplyDefaultMarkups(): void {
    this.commands.applyDefaultMarkups();
  }

  onMasterInfoChange(masterInfo: FormStateSnapshot<PbMasterInfo>): void {
    this.commands.setMasterInfo(masterInfo);
  }

  onDefaultsChange(defaults: FormStateSnapshot<PbDefaults>): void {
    this.commands.setDefaults(defaults);
  }

  onMemoChange(memo: string): void {
    this.commands.setRootField('memo', memo);
  }

  onTabSelect(tab: any): void {
    this.commands.selectTab(tab as PbDetailsTab);
  }

  onOpenReport(): void {
    openInNewWindow(this.reportUrl);
  }

  onBulkUpdateHPMarkups(): void {
    this.router.navigateToChild([`/estimating/price-books/${this.state.project(queryPriceBookId)}/bulk-update-hp-markups`]);
  }
  onBulkUpdateICMarkups(): void {
    this.router.navigateToChild([`/estimating/price-books/${this.state.project(queryPriceBookId)}/bulk-update-ic-markups`]);
  }
}
